import type { TemplateSummary } from "@tdelivr/api-client/generated/notifications/template.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { absent, at, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the replies board does to the templates NTF-07 returns, away from any rendering.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `macros`:
 *
 * ```
 * macros:['Replies and macros','SUP-04','Saved replies agents can send, with variables filled from the case.',
 *   [['Macros','34','done',''],['Missing Swahili','4','caution',''],
 *    ['Used today','188','done',''],['Retired','2','closed','']],
 *   ['Macro','Topic','Languages'],
 *   [['Payment still checking','payments','en · sw','done','live'],
 *    ['Refund on the way','refunds','en · sw','done','live'],
 *    ['Lost item: driver contacted','trips','en','caution','sw missing'],
 *    ['Account deletion started','privacy','en · sw','done','live']],
 *   'info','Variables that could be empty are flagged before sending.']
 * ```
 *
 * ## The recorded blocker was wrong on three of its four counts
 *
 * It read: _"Nothing on the platform models a reply macro: no name, no topic, no body in any
 * language. SUP-04, which SCREEN_SPEC.md cites, is the customer-360 read model — it holds no saved
 * reply. This is a service that does not exist, not a route that is not published."_
 *
 * The sentence about SUP-04 is right and is the valuable half: that service links a subject to a
 * case and holds nothing like a saved reply. **But a saved reply with variables, in a locale, with a
 * publication status, is exactly what NTF-07 holds** — `notification_templates` carries a
 * `template_key`, a `locale`, a `channel`, `allowed_variables`, a `content` body and a
 * `draft | published | retired` status, and ADR 0043 published a summary listing of it. So three of
 * the four things the note said are absent exist, and have since that service was written.
 *
 * This is R-17's rule applied to a citation rather than a rejection: **a rejection is a statement
 * about the question that was asked.** The question was "does SUP-04 hold a macro", and the answer
 * to it was correct.
 *
 * ## What is genuinely absent, and it is one column and one badge word
 *
 * | column      | source                                                                      |
 * | ----------- | --------------------------------------------------------------------------- |
 * | `Macro`     | `templateKey` — a key, not a name. The reports board's precedent             |
 * | `Topic`     | **nothing.** NTF-07 has no topic, and the key is not parsed to invent one    |
 * | `Languages` | `locale`, gathered across the versions of one key                           |
 *
 * **`Topic` is not drawn and the key is deliberately not parsed.** The design's `payments`,
 * `refunds`, `trips` and `privacy` look like the first segment of a key such as
 * `payments.still-checking` — and reading it that way would be this console inventing a taxonomy
 * NTF-07 does not have, which is the fault the finance console's reports board refused when it
 * declined to prettify a trigger key into a report name. A template whose key has no dot would get
 * no topic, and a template whose first segment is a service name would get a wrong one.
 *
 * **And `caution · sw missing` cannot be derived.** It requires knowing which locales a macro
 * *should* have, and nothing on this platform declares a tenant's expected set — so a template with
 * one locale is indistinguishable from one that only ever needed one. The design's
 * `Missing Swahili · 4` tile is the same absence counted.
 *
 * ## The board shows every message template, and says so
 *
 * NTF-07 does not mark a template as a support reply: there is no category, and `channel` is
 * `sms | email | push | in_app`, which is how a message is delivered rather than what it is for. So
 * this board answers **every template the tenant has**, which is the same shape the merchant
 * portal's products board carries — it reads the tenant's whole catalogue because COM-02's listing
 * is tenant-addressed — and it is recorded the same way: under the grid, in one sentence, with what
 * would fix it.
 *
 * The fix is one field: a category on a template, or a reserved key prefix the service itself
 * publishes. Either is a change to NTF-07 rather than to this screen.
 */

/** How many templates to take in one request. The contract's ceiling is 200. */
export const PER_FETCH = 200;

/** The scope this board spends, which ADR 0043 published and the tenant admin console also holds. */
export const MACROS_SCOPE = "template:summary:read";

/**
 * One macro as this board shows it: a key, with every locale and version NTF-07 holds under it.
 *
 * **The listing answers one row per `(key, locale, channel)` variant and the design draws one row
 * per macro**, so the rows are gathered here rather than rendered as published. That is the one
 * piece of arithmetic on this screen, and it is grouping rather than aggregation: no count is
 * computed and no figure is derived — the locales are collected and the status is taken from the
 * variants themselves.
 */
export interface Macro {
  readonly templateKey: string;
  /** Every locale NTF-07 holds a variant in, in the order the listing answered them. */
  readonly locales: readonly string[];
  /** Every channel those variants are delivered on, which is not the design's `Topic`. */
  readonly channels: readonly TemplateSummary["channel"][];
  /** The newest version across the variants, which is what the detail panel shows. */
  readonly version: number;
  /** When any variant of this macro last changed. */
  readonly updatedAt: string;
  /**
   * The macro's state, decided by its variants.
   *
   * `published` when any variant is, because a macro an agent can send is one with at least one
   * published variant. `retired` only when every variant is, because a macro with one retired
   * locale and one live one is still a macro an agent can send.
   */
  readonly status: TemplateSummary["status"];
}

/**
 * Gather the listing's variant rows into one row per macro.
 *
 * Insertion-ordered, so the board's order is the listing's order and nothing is re-sorted here —
 * the service answers newest version of each variant, and a board that re-sorted would disagree
 * with its own cursor.
 */
export function macrosOf(templates: readonly TemplateSummary[]): readonly Macro[] {
  const gathered = new Map<string, Macro>();
  for (const template of templates) {
    const held = gathered.get(template.templateKey);
    if (held === undefined) {
      gathered.set(template.templateKey, {
        templateKey: template.templateKey,
        locales: [template.locale],
        channels: [template.channel],
        version: template.version,
        updatedAt: template.updatedAt,
        status: template.status,
      });
      continue;
    }
    gathered.set(template.templateKey, {
      templateKey: held.templateKey,
      locales: held.locales.includes(template.locale)
        ? held.locales
        : [...held.locales, template.locale],
      channels: held.channels.includes(template.channel)
        ? held.channels
        : [...held.channels, template.channel],
      version: Math.max(held.version, template.version),
      updatedAt: held.updatedAt > template.updatedAt ? held.updatedAt : template.updatedAt,
      /*
       * `published` wins, then `draft`, then `retired`. A macro with one published locale is one an
       * agent can send; a macro whose only live variant was retired is retired.
       */
      status:
        held.status === "published" || template.status === "published"
          ? "published"
          : held.status === "draft" || template.status === "draft"
            ? "draft"
            : "retired",
    });
  }
  return [...gathered.values()];
}

/**
 * The design's `Languages` cell: the locales joined the way the design joins them.
 *
 * `en · sw`, which is the design's own separator. Sorted, because the listing's order is by key and
 * version rather than by locale, and two macros with the same two locales should read identically.
 */
export function languagesCell(macro: Macro): string {
  return [...macro.locales].sort().join(" · ");
}

/**
 * The state class and the word.
 *
 * `live` and `retired` are the design's own two; `draft` is NTF-07's third and the design has no
 * row for it, so it takes the design's `waiting` class and the service's word. The design's
 * `caution · sw missing` is not produced by anything — see this module's head.
 */
export function stateOf(macro: Macro): {
  key: StateClassKey;
  word: string;
  title: string;
} {
  switch (macro.status) {
    case "published":
      return {
        key: "done",
        word: "live",
        title:
          "At least one locale of this macro is published, so an agent could send it. NTF-07 publishes no record of a macro being used, so the design's `Used today` tile has no source.",
      };
    case "draft":
      return {
        key: "waiting",
        word: "draft",
        title:
          "Every locale of this macro is a draft. The design has no row for this state; `draft` is NTF-07's own word.",
      };
    case "retired":
      return {
        key: "closed",
        word: "retired",
        title:
          "Every locale of this macro is retired. NTF-07 publishes no delete, so a retired macro stays readable.",
      };
  }
}

/** The states present on the page, in board order, for the filter. */
export function statesPresent(macros: readonly Macro[]): readonly string[] {
  return [...new Set(macros.map((macro) => stateOf(macro).word))];
}

/** Whether a macro belongs under the chosen state filter. `undefined` is unfiltered. */
export function matchesState(macro: Macro, state: string | undefined): boolean {
  return state === undefined || stateOf(macro).word === state;
}

/** The row detail panel, which carries what the three columns cannot. */
export function macroDetail(macro: Macro): RecordDetail {
  const state = stateOf(macro);
  return {
    headline: macro.templateKey,
    identifier: macro.templateKey,
    source: "NTF-07",
    state: { classKey: state.key, label: state.word, code: "NTF-07", status: macro.status },
    fields: [
      absent(
        "Topic",
        "NTF-07 holds no topic on a template, and the key is deliberately not parsed into one: a macro whose key has no dot would get no topic, and one whose first segment is a service name would get a wrong one.",
      ),
      field("Languages", languagesCell(macro)),
      field("Delivered on", macro.channels.join(", ")),
      field("Newest version", `v${String(macro.version)}`),
      field("Last changed", at(macro.updatedAt) ?? macro.updatedAt),
      absent(
        "Body",
        "ADR 0043 withholds a template's content, subject and allowed variables from this listing: the render operation that interpolates a customer's own data is not published at all, and a body on a board is a message somebody could read without sending it.",
      ),
    ],
    note: "Every row here is a message template. NTF-07 does not mark one as a support reply, so this board cannot show only the macros an agent would send.",
  };
}

/**
 * Why the designed command cannot be sent.
 *
 * The design's primary action writes a macro. Every NTF-07 command is `/internal/v1` — the create,
 * the publish and the retire — and ADR 0043 keeps them there: a template's body is interpolated with
 * a customer's own data, and the render operation is not published either. So the button is drawn
 * and refused, as all four of this console's other boards draw and refuse theirs.
 */
export const NEW_MACRO_REFUSAL =
  "New macro is NTF-07's create command, which is on /internal/v1 and which this console holds no scope for. A macro's body is withheld from this board as well: ADR 0043 publishes a template's identity and state and not its content.";

/**
 * The design's closing note, kept word for word.
 *
 * `Variables that could be empty are flagged before sending.` It describes the send, which this
 * console cannot do and which NTF-07's render operation does not publish — so it is a statement
 * about the product rather than about a control on this screen, and is kept for the reason the
 * goodwill board keeps its own: the rule is the product's whether or not a screen enforces it.
 *
 * `allowedVariables` is the field that would make it checkable, and ADR 0043 withholds it from this
 * listing.
 */
export const BOARD_NOTE = "Variables that could be empty are flagged before sending.";

/**
 * The sentence under the grid, which carries the absence a reader is looking at.
 *
 * It says what the board is a board **of**, because that is the honest answer to a reader who
 * expected only support replies: NTF-07 does not distinguish one.
 */
export const LISTING_NOTE =
  "Every message template this tenant has, newest version of each language. NTF-07 does not mark a template as a support reply — there is no category, and its channel says how a message is delivered rather than what it is for — so this board cannot show only the macros an agent would send. One field on a template would fix it. The Topic column is empty for the same reason, and the design's sw missing state would need somebody to declare which languages a macro should have.";

/**
 * The four orders every board on this console offers, which are the design's own four.
 *
 * `az` and `za` sort the first column, which here is the macro's key. `default` is the order NTF-07
 * answered in — newest version of each variant — and the gathering preserves it.
 */
export type MacroOrder = "default" | "state" | "az" | "za";

export interface MacroQuery {
  readonly search: string;
  readonly order: MacroOrder;
  readonly page: number;
  readonly perPage: number;
}

export interface MacroView {
  readonly rows: readonly Macro[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly Macro[];
  readonly total: number;
  readonly pageCount: number;
}

/**
 * Filter, order and page what the screen holds.
 *
 * The search matches the key, the locales and the state word — so typing `sw` finds the macros that
 * have a Swahili variant, which is the question the design's own `Missing Swahili` tile is about and
 * the nearest a reader can get to it from published fields.
 */
export function macroPage(rows: readonly Macro[], query: MacroQuery, state?: string): MacroView {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (macro) =>
      matchesState(macro, state) &&
      (needle.length === 0 ||
        [macro.templateKey, languagesCell(macro), macro.channels.join(" "), stateOf(macro).word]
          .join(" ")
          .toLowerCase()
          .includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") return stateOf(left).word.localeCompare(stateOf(right).word);
          const compared = left.templateKey.localeCompare(right.templateKey);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The sentence under the grid, which says what was read and what is not in it.
 *
 * It counts **macros** rather than the variant rows the listing answered, because a reader looking
 * at a grid of macros counts what they can see.
 */
export function fetchedNote(count: number | undefined, truncated: boolean): string | undefined {
  if (count === undefined) return undefined;
  const read = count === 1 ? "1 macro" : `${String(count)} macros`;
  return truncated
    ? `${read}, gathered from the newest version of each language, and there are more.`
    : `${read}, gathered from the newest version of each language.`;
}
